import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import PdfTableComponent from '../PdfComponents/PdfTable';
import styles from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTotalBox from '../PdfComponents/PdfTotalBox';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfSupplierDetails from '../PdfComponents/PdfSupplierDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import { VouchersPdfPropsType } from './voucherHelpers';
import { PdfTotalBoxDataItem } from './VoucherServicePdfDocument';

const RefundServicePdfDocument: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            supplierName,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            pdfInfo: {
                companyInfo,
                supplierDetails,
                travelFileServiceTypePreviewName = '',
                code1code2 = '',
                voucherDetailsColumns,
                voucherDetailsRows,
                voucherPassengersColumns,
                voucherPassengersRows,
                passengersSummaryLine,
                hidePassengersTable = false,
                pdfTotalBoxData,
                reservationNumber,
                voucherCertification = 'Draft',
                remittanceNumber,
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                userName
            }
        },
        number: voucherNumber,
        type: voucherType,
        isPaid,
        createdAt
    } = props;

    const defaultAmount: number = props.info?.amount || 0;
    const defaultTax: number = props.info?.tax || 0;
    const defaultCurrencyCode: string = props.info?.currency?.code || '';

    const defaultPdfTotalBoxData: PdfTotalBoxDataItem[] = [
        { label: 'Amount', value: defaultAmount },
        { label: 'Tax', value: defaultTax },
        { label: `Total (${defaultCurrencyCode})`, value: defaultAmount + defaultTax }
    ];

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <PdfSupplierDetails
                                supplierName={supplierName}
                                supplierDetails={supplierDetails}
                            />
                            <PdfHeaderDetails
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                        {remittanceNumber ? (
                            <Text style={{ ...styles.littleText }}>Remittance No. {remittanceNumber}</Text>
                        ) : (
                            <></>
                        )}
                    </View>

                    <View style={{ ...styles.section, ...styles.alignCenter }}>
                        <Text style={{ ...styles.paymentStatus }}>{isPaid ? 'PAID' : ''}</Text>
                        <PdfMainDetails
                            topDetails={`${travelFileServiceTypePreviewName} ${voucherType} No. ${voucherNumber ||
                                ''}`}
                            bottomDetails={code1code2 ? `${code1code2?.code1}-${code1code2?.code2}` : ''}
                            withForLabel={code1code2 ? true : false}
                        />
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <Text style={[styles.marginBottom4px]}>
                            Kindly let us have tour credit note / refund for the amount due, as per the
                            following description
                        </Text>

                        <PdfTableComponent columns={voucherDetailsColumns} rows={voucherDetailsRows} />
                        {reservationNumber ? (
                            <View style={{ flexDirection: 'row', marginTop: 6 }}>
                                <Text style={{ color: '#777' }}>Reservation No:  </Text>
                                <Text style={{ fontWeight: 'bold' }}>{reservationNumber}</Text>
                            </View>
                        ) : <></>}
                    </View>

                    {/* A service with no per-passenger pricing has nothing to
                        itemise. Printing the list anyway meant either amounts
                        nobody entered (an even split of the refund) or a column
                        of 0.00 under a real Total. The voucher for the same
                        service prints no passenger table either. */}
                    {hidePassengersTable ? <></> : (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.marginBottom4px]}>For the following Passengers:</Text>
                            <PdfTableComponent
                                columns={voucherPassengersColumns}
                                rows={voucherPassengersRows}
                                summaryLine={passengersSummaryLine}
                            />
                        </View>
                    )}

                    <PdfTotalBox
                        pdfTotalBoxData={pdfTotalBoxData ? pdfTotalBoxData : defaultPdfTotalBoxData}
                    />

                    <View style={[styles.section, styles.marginBottom10px]} wrap={false}>
                        <Text style={[styles.littleText]}>This voucher is payable by {companyInfo.name}</Text>
                        <Text style={[styles.littleText]}>{companyInfo.phone}</Text>
                        <Text style={[styles.littleText]}>{companyInfo.website}</Text>
                    </View>

                    <PdfRemarksSection body={remark} />

                    <View style={styles.section}>
                        <Text>Issued by: {userName}</Text>
                    </View>
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(RefundServicePdfDocument);
